import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

export interface ExportColumn {
  key: string;
  label: string;
  width?: number;
  type?: 'string' | 'number' | 'date' | 'currency' | 'percentage';
}

export interface ExportData {
  [key: string]: any;
}

export interface ExportOptions {
  filename?: string;
  columns: ExportColumn[];
  data: ExportData[];
  title?: string;
  dateRange?: string;
  includeTimestamp?: boolean;
}

export class ExportService {
  static exportToCSV(options: ExportOptions): void {
    const { filename, columns, data, includeTimestamp = true } = options;
    
    // Create CSV headers
    const headers = columns.map(col => col.label).join(',');
    
    // Create CSV rows
    const rows = data.map(row => 
      columns.map(col => {
        const value = row[col.key];
        if (value === null || value === undefined) return '';
        
        // Format based on column type
        switch (col.type) {
          case 'date':
            return value instanceof Date ? format(value, 'yyyy-MM-dd HH:mm:ss') : value;
          case 'currency':
            return typeof value === 'number' ? `$${value.toFixed(2)}` : value;
          case 'percentage':
            return typeof value === 'number' ? `${value}%` : value;
          default:
            // Escape commas and quotes
            return typeof value === 'string' && (value.includes(',') || value.includes('"')) 
              ? `"${value.replace(/"/g, '""')}"` 
              : value;
        }
      }).join(',')
    );
    
    const csvContent = [headers, ...rows].join('\n');
    const timestamp = includeTimestamp ? `-${format(new Date(), 'yyyy-MM-dd-HHmm')}` : '';
    const finalFilename = `${filename || 'export'}${timestamp}.csv`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, finalFilename);
  }
  
  static exportToJSON(options: ExportOptions): void {
    const { filename, data, includeTimestamp = true } = options;
    
    const exportData = {
      exportDate: new Date().toISOString(),
      totalRecords: data.length,
      data: data
    };
    
    const jsonContent = JSON.stringify(exportData, null, 2);
    const timestamp = includeTimestamp ? `-${format(new Date(), 'yyyy-MM-dd-HHmm')}` : '';
    const finalFilename = `${filename || 'export'}${timestamp}.json`;
    
    const blob = new Blob([jsonContent], { type: 'application/json' });
    saveAs(blob, finalFilename);
  }
  
  static exportToExcel(options: ExportOptions): void {
    const { filename, columns, data, includeTimestamp = true } = options;
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Prepare data for Excel
    const wsData = [
      columns.map(col => col.label), // Headers
      ...data.map(row => 
        columns.map(col => {
          const value = row[col.key];
          if (value === null || value === undefined) return '';
          
          switch (col.type) {
            case 'date':
              return value instanceof Date ? value : new Date(value);
            case 'currency':
            case 'number':
              return typeof value === 'number' ? value : parseFloat(value) || 0;
            default:
              return value;
          }
        })
      )
    ];
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths
    ws['!cols'] = columns.map(col => ({ wch: col.width || 15 }));
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Export Data');
    
    // Generate filename
    const timestamp = includeTimestamp ? `-${format(new Date(), 'yyyy-MM-dd-HHmm')}` : '';
    const finalFilename = `${filename || 'export'}${timestamp}.xlsx`;
    
    // Save file
    XLSX.writeFile(wb, finalFilename);
  }
  
  static exportToPDF(options: ExportOptions & { 
    includeCharts?: boolean;
    reportSections?: any[];
  }): void {
    const { filename, columns, data, title, dateRange, includeTimestamp = true } = options;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPosition = 20;
    
    // Add header
    doc.setFontSize(20);
    doc.text(title || 'Export Report', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;
    
    if (dateRange) {
      doc.setFontSize(12);
      doc.text(`Date Range: ${dateRange}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
    }
    
    doc.setFontSize(10);
    doc.text(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 20;
    
    // Add summary
    doc.setFontSize(14);
    doc.text('Summary', 14, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.text(`Total Records: ${data.length}`, 14, yPosition);
    yPosition += 20;
    
    // Add table
    const tableColumns = columns.map(col => col.label);
    const tableRows = data.map(row => 
      columns.map(col => {
        const value = row[col.key];
        if (value === null || value === undefined) return '';
        
        switch (col.type) {
          case 'date':
            return value instanceof Date ? format(value, 'MM/dd/yyyy') : value;
          case 'currency':
            return typeof value === 'number' ? `$${value.toFixed(2)}` : value;
          case 'percentage':
            return typeof value === 'number' ? `${value}%` : value;
          default:
            return String(value);
        }
      })
    );
    
    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: yPosition,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 20 }
    });
    
    // Generate filename
    const timestamp = includeTimestamp ? `-${format(new Date(), 'yyyy-MM-dd-HHmm')}` : '';
    const finalFilename = `${filename || 'export'}${timestamp}.pdf`;
    
    doc.save(finalFilename);
  }
  
  static generatePreviewData(data: ExportData[], maxRows = 10): ExportData[] {
    return data.slice(0, maxRows);
  }
  
  static calculateFileSize(data: ExportData[], format: 'csv' | 'json' | 'excel' | 'pdf'): string {
    const dataString = JSON.stringify(data);
    const baseSize = new Blob([dataString]).size;
    
    let multiplier = 1;
    switch (format) {
      case 'csv':
        multiplier = 0.6; // CSV is more compact
        break;
      case 'json':
        multiplier = 1.2; // JSON has more overhead
        break;
      case 'excel':
        multiplier = 1.5; // Excel has more overhead
        break;
      case 'pdf':
        multiplier = 2.0; // PDF has significant overhead
        break;
    }
    
    const estimatedSize = baseSize * multiplier;
    
    if (estimatedSize < 1024) {
      return `${Math.round(estimatedSize)} B`;
    } else if (estimatedSize < 1024 * 1024) {
      return `${Math.round(estimatedSize / 1024)} KB`;
    } else {
      return `${(estimatedSize / (1024 * 1024)).toFixed(1)} MB`;
    }
  }
}